import { ChildContentType } from '@/constants/ChildContentType';
import {
  ChecklistModel,
  FactBoxModel,
  MediaModel,
  RichTextModel,
  SectionSeparatorModel,
  SideBySideModel,
} from '@/model/content';
import { Content } from '@/model/page/Content';
import { ArticleModel } from '@/model/page/Article';
import { ArticleResponse } from '@/model/response';
import { Identifiable } from '@/model/Identifiable';
import { mapPage, PageSharedFields } from '@/mapper/Page';
import { generateLinkHref } from '@/utils/generateLinkHref';
import { applyTranslations } from '@/mapper/utils/applyTranslations';

export const mapArticle = (pageResponse: ArticleResponse): ArticleModel => {
  const mappedPage = convertRelatedContent(pageResponse, mapPage(pageResponse));

  const response = applyTranslations(pageResponse, pageResponse.localization?.data?.content);
  const content = applyTranslations(response.content?.data?.content, response.content?.data?.localization);

  const useAnchorScrollNav = content?.useAnchorScrollNav;

  return {
    ...mappedPage,
    indices: useAnchorScrollNav ? generateIndices(mappedPage.children) : [],
    competencyAims: content?.competencyAims,
    globalAudio: content?.globalAudio,
    globalAudioTranscription: content?.globalAudioTranscription,
    bannerImage: {
      altText: content?.bannerImage?.altText,
      assetId: content?.bannerImage?.imageId,
      copyright: content?.bannerImage?.copyright,
    },
    linkToContentBlock: content?.linkToContentBlock?.items[0],
    linkToRelatedTest: content?.linkToRelatedTest?.items[0]
      ? {
          ...content?.linkToRelatedTest?.items[0],
          contentUrl: generateLinkHref(
            content?.linkToRelatedTest?.items[0]?.type,
            content?.linkToRelatedTest?.items[0]?.contentUrl,
          ),
        }
      : null,
    document: content?.cardDocuments?.documents?.[0],
    useAnchorScrollNav: useAnchorScrollNav,
  };
};

const convertRelatedContent = (response: ArticleResponse, article: PageSharedFields): PageSharedFields => {
  if (response.content?.data?.content?.relatedLinks?.items.length) {
    return {
      ...article,
      children: [mapRelatedContentToLinksBlock(response), ...article.children],
    };
  }

  return article;
};

const mapRelatedContentToLinksBlock = (response) => ({
  contentType: 'links-block',
  contentId: `${response.content?.data?.content?.id}-auto-generated-link-block`,
  content: {
    contentType: 'links-block',
    linksBlock: { items: response.content?.data?.content?.relatedLinks?.items },
    version: 'section',
  },
  id: `${response.content?.data?.content?.id}-auto-generated-link-block`,
});

const generateIndices = (children: Content[]) => {
  const excludedBlocks = [
    ChildContentType.ACCORDIONS,
    ChildContentType.AUDIO,
    ChildContentType.H5P,
    ChildContentType.IMAGE,
  ];
  const regexp = new RegExp(/(<h2 .*?id=")(?<id>.*?)(".*?>)(?<text>.*?)(<\/h2>)/, 'gim');
  let indices: Identifiable[] = [];
  children.forEach((child) => {
    if (child?.id && child?.contentId && !excludedBlocks.includes(child?.contentType as ChildContentType)) {
      if (
        child?.contentType === ChildContentType.TEXT &&
        ((child?.content as RichTextModel)?.content?.includes('<h2') ||
          (typeof child.content === 'string' && (child.content as string).includes('<h2')))
      ) {
        const results =
          (child?.content as RichTextModel)?.content?.match(regexp) ?? (child.content as string).match(regexp);
        results &&
          results.forEach((result) => {
            const match = regexp.exec(result);
            regexp.lastIndex = 0;

            match &&
              indices.push({
                id: match.groups.id,
                name: match.groups.text.replace(/(<([^>]+)>)/gim, ''),
              });
          });
      } else if (
        (child?.contentType === ChildContentType.LEFT_RIGHT ||
          child?.contentType === ChildContentType.ARTICLE_LEFT_RIGHT) &&
        child?.content
      ) {
        const mapColumnData = (column) => ({
          ...column,
          items:
            column.items?.map((item) => ({ ...item, contentId: item._id, contentType: item._type, id: item._id })) ??
            [],
        });
        const leftColumnData = mapColumnData((child?.content as SideBySideModel)?.columns[0]);
        const rightColumnData = mapColumnData((child?.content as SideBySideModel)?.columns[1]);

        if (leftColumnData && leftColumnData?.title?.length) {
          indices.push({
            id: leftColumnData._id,
            name: leftColumnData.title,
          });
        }

        const leftColumnIndices = generateIndices(leftColumnData.items);
        const rightColumnIndices = generateIndices(rightColumnData.items);

        indices = [...indices, ...leftColumnIndices, ...rightColumnIndices];
      } else {
        const childTitle =
          (child?.content as FactBoxModel)?.title ||
          (child?.content as ChecklistModel)?.heading ||
          (child?.content as MediaModel)?.name ||
          (child?.content as SectionSeparatorModel)?.text;

        if (childTitle && childTitle?.length) {
          indices.push({
            id: child.contentId,
            name: childTitle,
          });
        }
      }
    }
  });

  return indices;
};
